import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { OtpInputContainer, OtpInput } from './OtpInputGroup.styles';

const CODE_LENGTH = 6;
const INPUT_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

interface OtpInputGroupProps {
  /** Called once when all 6 digits are filled. Parent is responsible for verifying. */
  onComplete: (token: string) => void;
  /** Called on every keystroke with the current (possibly partial) value —
   *  use to clear error state or track the code for analytics. */
  onChange?: (value: string) => void;
  /** Set true while the parent is verifying — disables all inputs. */
  disabled?: boolean;
  /** Set true to show error border on inputs. */
  hasError?: boolean;
  /** Placeholder character for each empty box. Defaults to '•'. */
  placeholder?: string;
  /** Increment to reset the input (clears code and re-focuses first box). */
  resetCount?: number;
}

export const OtpInputGroup: React.FC<OtpInputGroupProps> = ({
  onComplete,
  onChange,
  disabled = false,
  hasError = false,
  placeholder = '•',
  resetCount = 0,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasSubmittedRef = useRef(false);
  const isComplete = code.every((digit: string) => digit !== '');

  // Auto-focus first input on mount and on reset.
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, [resetCount]);

  // Reset internal state when parent signals (e.g. after a failed verify).
  useEffect(() => {
    if (resetCount === 0) { return; }
    setCode(Array(CODE_LENGTH).fill(''));
    hasSubmittedRef.current = false;
  }, [resetCount]);

  // Auto-submit when all 6 digits are filled.
  useEffect(() => {
    if (isComplete && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      onComplete(code.join(''));
    }
  }, [isComplete, code, onComplete]);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();

  const fillCode = (digits: string, startIndex: number) => {
    const newCode = [...code];
    digits.split('').forEach((digit, i) => {
      if (startIndex + i < CODE_LENGTH) {
        newCode[startIndex + i] = digit;
      }
    });
    setCode(newCode);
    hasSubmittedRef.current = false;
    onChange?.(newCode.join(''));
    const nextEmpty = newCode.findIndex((d: string) => !d);
    focusInput(nextEmpty === -1 ? CODE_LENGTH - 1 : nextEmpty);
  };

  const handleChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      fillCode(digits.slice(0, CODE_LENGTH), 0);
      return;
    }
    const digit = digits.slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    hasSubmittedRef.current = false;
    onChange?.(newCode.join(''));
    if (digit && index < CODE_LENGTH - 1) { focusInput(index + 1); }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    fillCode(digits, index);
  };

  return (
    <OtpInputContainer data-testid="otp-input-container">
      {INPUT_POSITIONS.map((i) => (
        <OtpInput
          key={i}
          type="text"
          inputMode="numeric"
          maxLength={1}
          placeholder={placeholder}
          autoComplete="one-time-code"
          value={code[i]}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          ref={(el) => { inputRefs.current[i] = el; }}
          data-testid={`otp-input-${i}`}
          className={hasError ? 'error' : ''}
          disabled={disabled}
          aria-label={t('coverage.pre_onboarding.sms_verify.input_aria_label', { position: i + 1 })}
        />
      ))}
    </OtpInputContainer>
  );
};
